import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Star, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

/**
 * FavoriteStar — reusable star-toggle for reflections and sermons.
 *
 * Props:
 *   kind: 'reflection' | 'sermon'
 *   itemId: stable id (sermon.id or reflection title)
 *   itemPayload: snapshot stored at favoriting time so the Favorites page
 *                can render it even if the source changes.
 *   label: optional small text next to the star ("Save for later")
 *   showNoteButton: when true, exposes an extra 📝 button that opens a
 *                   notes modal (used on Sermon pages).
 *   className: extra wrapper classes.
 *   onChange: optional callback(favoritedBool) after any toggle.
 */
const FavoriteStar = ({
  kind,
  itemId,
  itemPayload,
  label,
  showNoteButton = false,
  className = '',
  onChange,
}) => {
  const { API } = useContext(AuthContext);
  const [favorited, setFavorited] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [notes, setNotes] = useState('');
  const [checking, setChecking] = useState(true);
  const [working, setWorking] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!itemId) return undefined;
    (async () => {
      try {
        const res = await axios.get(`${API}/favorites/check`, {
          params: { kind, item_id: itemId },
        });
        if (cancelled) return;
        setFavorited(!!res.data?.favorited);
        setFavoriteId(res.data?.id || null);
        setNotes(res.data?.notes || '');
      } catch {
        // Silent: if the check fails we just show the empty star
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API, kind, itemId]);

  const notify = (val) => {
    if (typeof onChange === 'function') onChange(val);
  };

  const handleToggle = async () => {
    if (working || !itemId) return;
    setWorking(true);
    try {
      if (favorited && favoriteId) {
        await axios.delete(`${API}/favorites/${favoriteId}`);
        setFavorited(false);
        setFavoriteId(null);
        setNotes('');
        toast.success('Removed from your collection');
        notify(false);
      } else {
        const res = await axios.post(`${API}/favorites`, {
          kind,
          item_id: itemId,
          item_payload: itemPayload || {},
        });
        setFavorited(true);
        setFavoriteId(res.data.id);
        setNotes(res.data.notes || '');
        toast.success('Saved to your collection');
        notify(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setWorking(false);
    }
  };

  const openNoteDialog = async () => {
    // Make sure it's favorited first (creates it silently if needed)
    if (!favorited) {
      await handleToggle();
    }
    setNoteDraft(notes || '');
    setNoteOpen(true);
  };

  const saveNote = async () => {
    if (!favoriteId) return;
    setWorking(true);
    try {
      const res = await axios.patch(`${API}/favorites/${favoriteId}`, {
        notes: noteDraft,
      });
      setNotes(res.data.notes || '');
      toast.success('Note saved');
      setNoteOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note');
    } finally {
      setWorking(false);
    }
  };

  const starClasses = `w-5 h-5 transition-colors ${
    favorited ? 'fill-amber-400 text-amber-500' : 'text-slate-400 hover:text-amber-500'
  }`;

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={checking || working}
        aria-pressed={favorited}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors ${
          checking || working ? 'opacity-60' : ''
        }`}
        data-testid={`favorite-star-${kind}-${itemId}`}
        title={favorited ? 'Remove from collection' : 'Save to collection'}
      >
        <Star className={starClasses} strokeWidth={favorited ? 2 : 1.75} />
        {label && (
          <span className="text-xs text-slate-600">
            {favorited ? 'Saved' : label}
          </span>
        )}
      </button>

      {showNoteButton && (
        <button
          type="button"
          onClick={openNoteDialog}
          disabled={working}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-slate-600 hover:bg-slate-100 transition-colors"
          data-testid={`favorite-note-${kind}-${itemId}`}
          title="Add a personal note"
        >
          <StickyNote className="w-4 h-4" />
          {notes ? 'Edit note' : 'Add note'}
        </button>
      )}

      {showNoteButton && (
        <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Your note</DialogTitle>
              <DialogDescription>
                Capture what spoke to you — this stays in your personal collection.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              rows={6}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              maxLength={4000}
              placeholder="What stood out? Where did the Spirit stir something in you?"
              data-testid="favorite-note-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNoteOpen(false)} disabled={working}>
                Cancel
              </Button>
              <Button
                onClick={saveNote}
                disabled={working}
                className="bg-[#2B6B7F]"
                data-testid="favorite-note-save"
              >
                {working ? 'Saving…' : 'Save note'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default FavoriteStar;
